/**
 * BattleCode V2 — Pyodide Dual Worker Runtime
 *
 * V2 核心改造：2 个常驻 Worker（Player + AI），物理隔离并行。
 * 替代 pyodide-pool.ts 在战斗期的 acquire/release 模式。
 *
 * ADR-002 方案 B：2 个独立 Pyodide Worker，Promise.all 并行执行双方 think()。
 *
 * 关键设计：
 *   - Worker A（玩家代码）+ Worker B（AI 代码），各自独立的 Pyodide 实例
 *   - 初始化时并行创建两个 Worker + 并行 loadPyodide
 *   - 异常隔离：一方崩溃不影响另一方继续运行
 *   - 连续 3 次异常 → 抛出 BattleError 终止战斗
 *   - 低内存设备（<2GB）降级为串行模式
 *   - 复用现有 pyodide-worker.ts（不改动），协议兼容
 *
 * 公共 API：
 *   - init(playerCode, aiCode, onProgress) → Promise<void>
 *   - runPlayerThink(state, tickNum) → Promise<BotTickResult>
 *   - runAIThink(state, tickNum) → Promise<BotTickResult>
 *   - runBothThink(playerCode, aiCode, playerState, aiState, tickNum) → Promise<{player, ai}>
 *   - dispose() → void
 */

import PyodideWorker from "./pyodide-worker?worker";
import type { BattleState, BotTickResult, WorkerInbound, WorkerOutbound } from "./types";

// ===== 常量 =====
const MAX_CONSECUTIVE_ERRORS = 3;
const HARD_TIMEOUT_MS = 200;
const LOW_MEMORY_THRESHOLD_GB = 2;

/** 战斗因 Worker 异常终止 */
export class BattleError extends Error {
  constructor(
    message: string,
    public readonly source: "player" | "ai" | "both",
  ) {
    super(message);
    this.name = "BattleError";
  }
}

// ===== 内部：Worker 句柄 =====
interface WorkerHandle {
  botId: string;
  worker: Worker;
  botCode: string;
  /** V3: 多坦克代码数组（botCodes[i] = 第 i 个坦克的代码） */
  botCodes?: string[];
  isAI: boolean;
  initDone: boolean;
  initError: string | null;
  pendingResolves: Map<number, (result: BotTickResult) => void>;
  runtimeErrorCount: number;
  consecutiveErrors: number;
  /** ready 事件是否已收到（Pyodide 运行时就绪） */
  pyodideReady: boolean;
  /** init 是否正在进行中（防止并发 init） */
  initInFlight: boolean;
  /** rebuild 是否正在进行中（防止并发 rebuild） */
  rebuildInFlight?: boolean;
}

export class PyodideDualRuntime {
  private workerA: WorkerHandle | null = null;
  private workerB: WorkerHandle | null = null;
  private lowMemoryMode: boolean = false;
  private disposed: boolean = false;

  constructor() {
    // 检测低内存设备
    if (typeof navigator !== "undefined" && "deviceMemory" in navigator) {
      const mem = (navigator as any).deviceMemory as number;
      if (mem < LOW_MEMORY_THRESHOLD_GB) {
        this.lowMemoryMode = true;
        console.warn(
          `[PyodideDualRuntime] 低内存设备 (${mem}GB < ${LOW_MEMORY_THRESHOLD_GB}GB)，降级为串行模式`,
        );
      }
    }
  }

  // ===== 公共 API =====

  /**
   * 并行初始化两个 Worker。
   * 调用时机：屏 4 战斗开始前（屏 2 点击 "FIGHT" 后）。
   *
   * @param playerCode 玩家 bot Python 代码（来自屏 2 编辑器）
   * @param aiCode AI bot Python 代码（来自 Level 的 ai.py）
   * @param onProgress 进度回调，用于屏 4 显示加载进度
   */
  async init(
    playerCode: string | string[],
    aiCode: string,
    onProgress?: (msg: string) => void,
  ): Promise<void> {
    if (this.disposed) throw new Error("PyodideDualRuntime already disposed");

    // 1) 并行创建两个 Worker
    onProgress?.("初始化 Python 运行时 (1/2)...");
    const [rawA, rawB] = await Promise.all([
      this.createWorker(),
      this.createWorker(),
    ]);

    this.workerA = this.makeHandle(rawA, "player_a", playerCode, false);
    this.workerB = this.makeHandle(rawB, "ai_b", aiCode, true);

    // 2) 并行 init Pyodide（两个 Worker 各自 loadPyodide）
    //    用计数器跟踪哪个先完成，精确触发 progress 回调
    let initDoneCount = 0;
    const onWorkerInitDone = () => {
      initDoneCount++;
      if (initDoneCount === 1) {
        onProgress?.("初始化 Python 运行时 (2/2)...");
      }
    };

    try {
      await Promise.all([
        this.initWorker(this.workerA, onWorkerInitDone),
        this.initWorker(this.workerB, onWorkerInitDone),
      ]);
    } catch (e: any) {
      // 任一 Worker init 失败 → 清理并抛错
      this.dispose();
      throw new Error(`PyodideDualRuntime init failed: ${e.message}`);
    }
  }

  /**
   * 玩家 Worker A 执行 think。
   * @returns tick 结果；异常时返回 idle 兜底
   */
  async runPlayerThink(state: BattleState, tickNum: number): Promise<BotTickResult> {
    if (!this.workerA) throw new Error("Worker A not initialized");
    return this.sendTick(this.workerA, state, tickNum);
  }

  /**
   * AI Worker B 执行 think。
   * @returns tick 结果；异常时返回 idle 兜底
   */
  async runAIThink(state: BattleState, tickNum: number): Promise<BotTickResult> {
    if (!this.workerB) throw new Error("Worker B not initialized");
    return this.sendTick(this.workerB, state, tickNum);
  }

  /**
   * V3: 多坦克模式下向 Worker 发送 tick_batch（团队状态）。
   * Worker 内部遍历所有存活坦克，依次调用 think(self)，返回 actions 数组。
   */
  async runTeamThink(side: "player" | "ai", state: BattleState, tickNum: number, messageInbox?: Record<number, Array<{ from: number; data: any }>>): Promise<BotTickResult> {
    const handle = side === "player" ? this.workerA : this.workerB;
    if (!handle) throw new Error(`Worker ${side} not initialized`);
    return new Promise((resolve, reject) => {
      if (!handle.initDone) {
        resolve({ action: { type: "idle" }, logs: ["[worker not ready]"], duration_ms: 0, error: null, timed_out: false });
        return;
      }

      const watchdog = window.setTimeout(() => {
        handle.consecutiveErrors++;
        handle.runtimeErrorCount++;
        handle.pendingResolves.delete(tickNum);
        this.rebuildWorker(handle);
        if (handle.consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          reject(new BattleError(`[${handle.botId}] ${MAX_CONSECUTIVE_ERRORS} consecutive errors`, handle.isAI ? "ai" : "player"));
          return;
        }
        resolve({ action: { type: "idle" }, logs: ["[hard timeout]"], duration_ms: HARD_TIMEOUT_MS, error: "hard_timeout", timed_out: true });
      }, 2000); // 多坦克模式 2s（3v3 handleTickBatch 挂起时防止无限等待）

      handle.pendingResolves.set(tickNum, (result) => {
        clearTimeout(watchdog);
        if (result.error && handle.consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          reject(new BattleError(`[${handle.botId}] consecutive errors: ${result.error}`, handle.isAI ? "ai" : "player"));
          return;
        }
        resolve(result);
      });

      handle.worker.postMessage({ type: "tick_batch", state, tickNum, message_inbox: messageInbox } as WorkerInbound);
    });
  }

  /**
   * V3: 多坦克并行执行双方 think。
   * 低内存设备降级为串行。
   *
   * @returns 双方 think 结果。单方异常不影响对方。
   */
  async runBothThink(
    playerState: BattleState,
    aiState: BattleState,
    tickNum: number,
  ): Promise<{
    player: BotTickResult;
    ai: BotTickResult;
  }> {
    if (!this.workerA || !this.workerB) {
      throw new Error("Workers not initialized");
    }

    if (this.lowMemoryMode) {
      // 串行模式：先跑玩家，再跑 AI
      const playerResult = await this.runPlayerThink(playerState, tickNum);
      const aiResult = await this.runAIThink(aiState, tickNum);
      return { player: playerResult, ai: aiResult };
    }

    // 并行模式：Promise.all
    const [playerResult, aiResult] = await Promise.all([
      this.runPlayerThink(playerState, tickNum),
      this.runAIThink(aiState, tickNum),
    ]);
    return { player: playerResult, ai: aiResult };
  }

  /**
   * 检查指定 Worker 是否健康（initDone 且无连续异常）。
   */
  isWorkerHealthy(side: "player" | "ai"): boolean {
    const h = side === "player" ? this.workerA : this.workerB;
    if (!h) return false;
    return h.initDone && h.initError === null && h.consecutiveErrors < MAX_CONSECUTIVE_ERRORS;
  }

  /**
   * 战斗结束，terminate 两个 Worker 并清理所有状态。
   */
  dispose(): void {
    this.disposed = true;
    if (this.workerA) {
      this.terminateHandle(this.workerA);
      this.workerA = null;
    }
    if (this.workerB) {
      this.terminateHandle(this.workerB);
      this.workerB = null;
    }
  }

  // ===== 内部：Worker 生命周期 =====

  /** 创建原始 Worker 实例 */
  private createWorker(): Promise<Worker> {
    return new Promise((resolve) => {
      const w = new PyodideWorker();
      // 挂 error 监听（防止 worker import 阶段静默失败）
      w.addEventListener("error", (e) => {
        console.error("[PyodideDualRuntime] worker onerror:", e.message);
      });
      w.addEventListener("messageerror", (e) => {
        console.error("[PyodideDualRuntime] worker messageerror:", e);
      });
      resolve(w);
    });
  }

  /** 创建 WorkerHandle */
  private makeHandle(worker: Worker, botId: string, botCode: string | string[], isAI: boolean): WorkerHandle {
    const codes = Array.isArray(botCode) ? botCode : [botCode];
    const handle: WorkerHandle = {
      botId,
      worker,
      botCode: codes[0],
      botCodes: codes,
      isAI,
      initDone: false,
      initError: null,
      pendingResolves: new Map(),
      runtimeErrorCount: 0,
      consecutiveErrors: 0,
      pyodideReady: false,
      initInFlight: false,
    };
    this.attachHandlers(handle);
    return handle;
  }

  /** 挂 onmessage/onerror handler */
  private attachHandlers(handle: WorkerHandle): void {
    const { worker, botId } = handle;

    worker.onmessage = (e: MessageEvent<WorkerOutbound>) => {
      const msg = e.data;
      switch (msg.type) {
        // ★ Bug #4 修复：ready 分支由 initWorker 独占处理，不在 attachHandlers 中发送 init
        //   之前 attachHandlers 和 initWorker 都监听 ready → Worker 收到 2 个 init 消息
        //   → 双重初始化 → 第二个 init 覆盖第一个 → 间歇性 init_done 不发 → 15s timeout
        //   现在：initWorker 的 messageHandler 是唯一处理 ready → 发 init 的路径
        //   attachHandlers 仅处理 init_done / tick_result / error / diag_log / stdout / stderr

        case "init_done":
          handle.initDone = true;
          handle.initError = msg.error;
          handle.initInFlight = false;
          break;

        case "tick_result": {
          const resolver = handle.pendingResolves.get(msg.tickNum);
          if (resolver) {
            handle.pendingResolves.delete(msg.tickNum);
            resolver(msg.result);
          }
          if (msg.result.error) {
            handle.runtimeErrorCount++;
            handle.consecutiveErrors++;
          } else {
            // 成功则重置连续错误计数
            handle.consecutiveErrors = 0;
          }
          break;
        }

        case "error":
          handle.runtimeErrorCount++;
          handle.consecutiveErrors++;
          console.error(`[${botId}] worker error:`, msg.error);
          break;

        case "diag_log":
          // 2026-07-11:info 级 diag_log 不再透传(每 tick 6+ 条 [BC-DIAG] → browser.log 膨胀)
          //   仅保留 warn/error 级
          if (msg.level === "info") break;
          {
            const m = `[worker:${msg.botId}] ${msg.msg}`;
            if (msg.level === "warn") console.warn(m);
            else if (msg.level === "error") console.error(m);
          }
          break;

        case "stdout":
        case "stderr":
          // stdout/stderr 由 engine-scheduler 的 onLog 处理，
          // 这里不处理，让 engine-scheduler 自行监听
          break;
      }
    };

    worker.onerror = (e: ErrorEvent) => {
      console.error(`[${botId}] worker onerror:`, e);
      handle.runtimeErrorCount++;
      handle.consecutiveErrors++;
    };
  }

  /**
   * 初始化单个 Worker：等 ready → 发 init → 等 init_done。
   * @param onReady 回调：Worker 收到 ready 时触发（用于 progress 跟踪）
   */
  private initWorker(handle: WorkerHandle, onReady?: () => void): Promise<void> {
    if (handle.initInFlight) {
      return Promise.reject(new Error(`Worker ${handle.botId} init already in flight`));
    }
    handle.initInFlight = true;

    return new Promise((resolve, reject) => {
      // ★ Bug #4 修复：分段计时，避免 cold-start 耗尽 init 超时预算
      //   之前：15s safety timer 在 initWorker 调用时就启动
      //   问题：Worker cold start（loadPyodide）需 2-10s，15s 计时器已耗掉大半
      //   修复：阶段 1 等 ready（30s cold-start 预算），阶段 2 等 init_done（15s init 预算）

      let phase1Timer: ReturnType<typeof setTimeout> | null = null;
      let phase2Timer: ReturnType<typeof setTimeout> | null = null;
      const PHASE1_TIMEOUT_MS = 30_000; // 等 Pyodide ready（本地 2-5s，CDN 5-15s，30s 足够）
      const PHASE2_TIMEOUT_MS = 15_000; // 等 init_done（bot code 注入 + think 验证，通常 <1s）

      const messageHandler = (e: MessageEvent<WorkerOutbound>) => {
        const msg = e.data;
        if (msg.type === "ready") {
          // 阶段 1 完成 → 取消 phase1 timer，启动 phase2 timer
          if (phase1Timer) { clearTimeout(phase1Timer); phase1Timer = null; }
          handle.pyodideReady = true;
          onReady?.();

          phase2Timer = setTimeout(() => {
            handle.worker.removeEventListener("message", messageHandler);
            handle.initInFlight = false;
            reject(new Error(`Worker ${handle.botId} init_done timeout (${PHASE2_TIMEOUT_MS / 1000}s after ready)`));
          }, PHASE2_TIMEOUT_MS);

          // 发送 init 注入 bot code（V3: 支持多代码）
          handle.worker.postMessage({
            type: "init",
            botCode: handle.botCode,
            botCodes: handle.botCodes,
            isAI: handle.isAI,
            botId: handle.botId,
          } as WorkerInbound);
        } else if (msg.type === "init_done") {
          if (phase2Timer) { clearTimeout(phase2Timer); phase2Timer = null; }
          handle.worker.removeEventListener("message", messageHandler);
          handle.initDone = true;
          handle.initError = msg.error;
          handle.initInFlight = false;
          if (msg.error) {
            reject(new Error(`Worker ${handle.botId} init error: ${msg.error}`));
          } else {
            resolve();
          }
        } else if (msg.type === "error" && (msg as any).phase === "init") {
          if (phase1Timer) { clearTimeout(phase1Timer); phase1Timer = null; }
          if (phase2Timer) { clearTimeout(phase2Timer); phase2Timer = null; }
          handle.worker.removeEventListener("message", messageHandler);
          handle.initInFlight = false;
          reject(new Error(`Worker ${handle.botId} init error: ${msg.error}`));
        }
      };

      // 阶段 1：等 ready（Pyodide cold start）
      phase1Timer = setTimeout(() => {
        handle.worker.removeEventListener("message", messageHandler);
        handle.initInFlight = false;
        reject(new Error(`Worker ${handle.botId} ready timeout (${PHASE1_TIMEOUT_MS / 1000}s, Pyodide cold start)`));
      }, PHASE1_TIMEOUT_MS);

      handle.worker.addEventListener("message", messageHandler);
    });
  }

  // ===== 内部：tick 调度 =====

  /**
   * 向 Worker 发送 tick 并等待结果。
   * 包含硬超时 watchdog + 异常恢复逻辑。
   */
  private sendTick(handle: WorkerHandle, state: BattleState, tickNum: number): Promise<BotTickResult> {
    return new Promise((resolve, reject) => {
      // Worker 未就绪 → idle 兜底
      if (!handle.initDone) {
        resolve({
          action: { type: "idle" },
          logs: ["[worker not ready]"],
          duration_ms: 0,
          error: null,
          timed_out: false,
        });
        return;
      }

      // 硬超时 watchdog（200ms）
      const watchdog = window.setTimeout(() => {
        console.warn(`[${handle.botId}] hard timeout @ tick ${tickNum}, rebuilding worker`);
        // A1: hard timeout 路径也要递增错误计数，与 onmessage tick_result 分支保持一致
        handle.consecutiveErrors++;
        handle.runtimeErrorCount++;
        handle.pendingResolves.delete(tickNum);
        this.rebuildWorker(handle);
        // A6: hard timeout 后检查是否达到阈值 → reject BattleError
        if (handle.consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          reject(new BattleError(
            `[${handle.botId}] ${MAX_CONSECUTIVE_ERRORS} consecutive errors (hard timeout @ tick ${tickNum})`,
            handle.isAI ? "ai" : "player",
          ));
          return;
        }
        resolve({
          action: { type: "idle" },
          logs: ["[hard timeout]"],
          duration_ms: HARD_TIMEOUT_MS,
          error: "hard_timeout",
          timed_out: true,
        });
      }, HARD_TIMEOUT_MS);

      handle.pendingResolves.set(tickNum, (result) => {
        clearTimeout(watchdog);

        // 检测连续异常 → 抛出 BattleError
        if (result.error && handle.consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          console.error(
            `[${handle.botId}] ${MAX_CONSECUTIVE_ERRORS} consecutive errors, terminating battle`,
          );
          // A6: reject BattleError 而非 resolve idle，让 engine-scheduler catch 分支处理
          reject(new BattleError(
            `[${handle.botId}] ${MAX_CONSECUTIVE_ERRORS} consecutive errors: ${result.error}`,
            handle.isAI ? "ai" : "player",
          ));
          return;
        }

        resolve(result);
      });

      handle.worker.postMessage({ type: "tick", state, tickNum } as WorkerInbound);
    });
  }

  // ===== 内部：Worker 异常恢复 =====

  /**
   * 重建崩溃的 Worker。
   * 1) terminate 旧 Worker
   * 2) 创建新 Worker
   * 3) 重新挂 handlers
   * 4) 重新 init（注入 bot-api.py + bot code）
   *
   * 重建期间该 bot 的 tick 返回 idle（initDone=false 兜底）。
   * 连续 3 次异常 → 外部应调用 dispose() 终止战斗。
   */
  private async rebuildWorker(handle: WorkerHandle): Promise<void> {
    // A3: 互斥保护 — 防止 watchdog 和正常异常路径同时触发 rebuild
    if (handle.rebuildInFlight) return;
    handle.rebuildInFlight = true;

    try {
    const { botId } = handle;

    // 1) terminate 旧 Worker
    try {
      handle.worker.terminate();
    } catch {
      // ignore
    }

    // 2) 清除旧的 pending resolves（全部 resolve 为 idle）
    handle.pendingResolves.forEach((resolve) => {
      resolve({
        action: { type: "idle" },
        logs: ["[worker rebuild]"],
        duration_ms: 0,
        error: "worker_rebuilt",
        timed_out: false,
      });
    });
    handle.pendingResolves.clear();

    // 3) 创建新 Worker
    const newWorker = await this.createWorker();

    // 4) 更新 handle
    handle.worker = newWorker;
    handle.initDone = false;
    handle.initError = null;
    handle.pyodideReady = false;
    handle.initInFlight = false;
    this.attachHandlers(handle);

    // 5) 重新 init（异步，不阻塞当前 tick）
    console.warn(`[${botId}] worker rebuilt, re-initializing...`);
    this.initWorker(handle).catch((e) => {
      console.error(`[${botId}] rebuild init failed:`, e);
      handle.initError = e.message;
      handle.initDone = true; // 标记完成（虽然是失败），让 sendTick 走 idle 兜底
    });
    } finally {
      handle.rebuildInFlight = false;
    }
  }

  /** 终止 Worker 并清理 handle */
  private terminateHandle(handle: WorkerHandle): void {
    // 清除所有 pending resolves
    handle.pendingResolves.forEach((resolve) => {
      resolve({
        action: { type: "idle" },
        logs: ["[worker disposed]"],
        duration_ms: 0,
        error: "worker_disposed",
        timed_out: false,
      });
    });
    handle.pendingResolves.clear();

    try {
      handle.worker.terminate();
    } catch {
      // ignore
    }
  }
}
